// src/hooks/useElementsSuivis.ts — SCE Issues
// Les projets (Lot 12 bis) et équipements (Lot 12 ter) que suit une personne.
//
// Lus dans SA ligne de SCE_UserPrefs, les deux colonnes en une requête. Une
// fiche projet ou équipement ouverte PAR-DESSUS l'accueil (surimpression au
// bureau) change le suivi alors que l'accueil reste monté : l'écriture diffuse
// donc la nouvelle liste à toutes les instances du hook, et « Éléments
// suivis » se met à jour sans rechargement.

import { useCallback, useEffect, useState } from 'react'
import { changerSuivi, lireElementsSuivis } from '@/api/userPrefs.api'
import { AUCUN_ELEMENT_SUIVI, type ElementsSuivis, type GenreSuivi } from '@/utils/elementsSuivis'

const EVENEMENT = 'sce:elements-suivis-modifies'

interface DetailEvenement {
  upn: string
  genre: GenreSuivi
  liste: string[]
}

export function useElementsSuivis(upn: string | undefined): {
  elements: ElementsSuivis
  /** Faux tant que les listes ne sont pas lues. */
  lus: boolean
  changer: (genre: GenreSuivi, id: string, suivre: boolean) => Promise<void>
} {
  const [elements, setElements] = useState<ElementsSuivis>(AUCUN_ELEMENT_SUIVI)
  const [lus, setLus] = useState(false)

  useEffect(() => {
    setElements(AUCUN_ELEMENT_SUIVI)
    setLus(false)
    if (!upn) return undefined
    let actif = true
    lireElementsSuivis(upn)
      .then((e) => { if (actif) setElements(e) })
      .catch((err: unknown) => {
        // Accueil : sans les listes, on montre les seuls problèmes suivis.
        // eslint-disable-next-line no-console
        console.warn('[useElementsSuivis] lecture impossible:', err)
      })
      .finally(() => { if (actif) setLus(true) })

    const ecouter = (e: Event): void => {
      const d = (e as CustomEvent<DetailEvenement>).detail
      if (d.upn.toLowerCase() !== upn.toLowerCase()) return
      setElements((avant) => (d.genre === 'projet' ? { ...avant, projets: d.liste } : { ...avant, equipements: d.liste }))
    }
    window.addEventListener(EVENEMENT, ecouter)
    return () => {
      actif = false
      window.removeEventListener(EVENEMENT, ecouter)
    }
  }, [upn])

  const changer = useCallback(async (genre: GenreSuivi, id: string, suivre: boolean): Promise<void> => {
    if (!upn) return
    const liste = await changerSuivi(upn, genre, id, suivre)
    window.dispatchEvent(new CustomEvent<DetailEvenement>(EVENEMENT, { detail: { upn, genre, liste } }))
  }, [upn])

  return { elements, lus, changer }
}
